import { http } from '../../services/http';
import type { FeatureState } from '../auth/types';

export interface PlatformTenant {
  id: string;
  slug: string;
  name: string;
  legalName: string | null;
  businessCategory: string;
  status: 'pending_setup' | 'active' | 'suspended' | 'cancelled';
  contactEmail: string | null;
  onboardingStep: string;
  suspendedReason: string | null;
  createdAt: string;
  staffCount?: number;
  plan?: { code: string; name: string } | null;
}

export interface TenantDetail extends PlatformTenant {
  subscription: {
    id: string;
    status: string;
    startsAt: string;
    trialEndsAt?: string | null;
    currentPeriodEndsAt?: string | null;
    graceEndsAt?: string | null;
    readOnly?: boolean;
    plan: { code: string; name: string } | null;
  } | null;
  featureOverrides: { key: string; enabled: boolean; limit: number | null; reason: string | null }[];
  entitlements: Record<string, FeatureState>;
  owner: { id: string; fullName: string; email: string; status: string } | null;
  counts: { staff: number; branches: number };
}

export interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  status: 'active' | 'retired';
  priceAmount: string | null;
  currency: string;
  billingInterval: string;
  trialDays: number;
  features: Record<string, { enabled: boolean; limit: number | null }>;
}

export const platformApi = {
  tenants: (query: { page?: number; q?: string; status?: string }) =>
    http.list<PlatformTenant[]>('/platform/tenants', { query: { pageSize: 25, ...query } }),
  tenant: (id: string) => http.get<TenantDetail>(`/platform/tenants/${id}`),
  createTenant: (body: Record<string, unknown>) =>
    http.post<TenantDetail & { ownerInviteEmailSent: boolean }>('/platform/tenants', body),
  setStatus: (id: string, action: 'activate' | 'suspend' | 'cancel', reason?: string) =>
    http.post<TenantDetail>(`/platform/tenants/${id}/${action}`, reason ? { reason } : {}),
  resendOwnerInvite: (id: string) =>
    http.post<{ emailSent: boolean }>(`/platform/tenants/${id}/owner-invitation`),
  setSubscription: (id: string, planCode: string) =>
    http.put<TenantDetail>(`/platform/tenants/${id}/subscription`, { planCode }),
  setFeatures: (
    id: string,
    features: Record<string, { enabled: boolean; limit?: number | null; reason?: string } | null>,
  ) => http.put<TenantDetail>(`/platform/tenants/${id}/features`, { features }),
  plans: () => http.get<Plan[]>('/platform/plans'),
  updatePlan: (id: string, body: Record<string, unknown>) => http.patch<Plan>(`/platform/plans/${id}`, body),
  overview: () =>
    http.get<{ tenantsByStatus: Record<string, number>; staffProfiles: number }>('/platform/overview'),
};

export const platformKeys = {
  tenants: (query: object) => ['platform', 'tenants', query] as const,
  tenant: (id: string) => ['platform', 'tenant', id] as const,
  plans: ['platform', 'plans'] as const,
  overview: ['platform', 'overview'] as const,
};

export const FEATURE_LABELS: Record<string, string> = {
  'limit.branches': 'Branches',
  'limit.staff': 'Staff',
  'limit.leads_monthly': 'Leads per month',
  'limit.integrations': 'Integrations',
  'module.public_forms': 'Public lead forms',
  'module.whatsapp': 'WhatsApp',
  'module.auto_assignment': 'Auto-assignment',
  'module.api_access': 'API access',
  'module.reports_advanced': 'Advanced reports',
};

export const CATEGORY_LABELS: Record<string, string> = {
  real_estate: 'Real estate',
  salon: 'Salon',
  gym: 'Gym / fitness',
  education: 'Coaching & education',
  clinic: 'Clinic',
  automobile: 'Automobile dealer',
  agency: 'Agency',
  general: 'General sales',
  other: 'Other',
};
